import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import classNames from "classnames";
import CheckBox from "../common/Check-box";

// SVGs for navigation
const ChevronRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18L15 12L9 6" />
  </svg>
);

const ChevronLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18L9 12L15 6" />
  </svg>
);

const ArrowDownGray = ({ width = 8, height = 6, color = "#92929D", ...props }) => (
  <svg width={width} height={height} viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.86496 0C7.7128 0 8.17595 0.988857 7.63318 1.64018L4.76822 5.07814C4.36843 5.55789 3.63157 5.55789 3.23178 5.07813L0.366819 1.64018C-0.175953 0.988856 0.287204 0 1.13504 0L6.86496 0Z"
      fill={color}
    />
  </svg>
);

const BaseCalendar = ({
  date,
  setDate,
  placeholder,
  isEndDateCalendar = false,
  trackStartDate = new Date(),
  handleEndDateUpdate = (date) => { },
  isDisabled = false,
  dateToShow = "",
  showTimeSelect = false,
  showTimeSelectOnly = false,
  timeIntervals = 15,
  isTimePicker,
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isEndDateCalendar) {
      handleEndDateUpdate && handleEndDateUpdate(date);
    }
  }, [date]);

  const CalendarInput = forwardRef(({ value, onClick, className, placeholder, disabled }, ref) => (
    <div className={`${className} calendar-input`} onClick={() => setOpen(!open)} ref={ref}>
      <input
        readOnly={true}
        placeholder=""
        spellCheck={false}
        value={isDisabled ? "Set End Date" : value}
        type="button"
        disabled={disabled}
      />
      <ArrowDownGray className="arrow" />
    </div>
  ));

  const renderHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", userSelect: "none" }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          decreaseMonth();
        }}
        disabled={prevMonthButtonDisabled}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", marginLeft: "0px", marginRight: "24px" }}
      >
        <ChevronLeft />
      </button>
      <span style={{ fontWeight: 500, fontSize: "15px" }}>
        {date.toLocaleString("default", { month: "long", year: "numeric" })}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          increaseMonth();
        }}
        disabled={nextMonthButtonDisabled}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
      >
        <ChevronRight />
      </button>
    </div>
  );

  return (
    <DatePicker
      selected={date}
      open={open}
      onSelect={() => setOpen(false)}
      onClickOutside={() => setOpen(false)}
      showPopperArrow={false}
      closeOnScroll={false}
      popperPlacement="top-start"
      {...(isTimePicker
        ? {
          showTimeSelect,
          showTimeSelectOnly,
          timeIntervals,
          dateFormat: "h:mm aa",
        }
        : {
          minDate: isEndDateCalendar
            ? new Date(trackStartDate.getFullYear(), trackStartDate.getMonth(), trackStartDate.getDate() + 1)
            : new Date(),
          calendarStartDay: 1,
          holidays: dateToShow !== "" ? [{ date: dateToShow }] : [],
          dateFormat: "dd/MM/yyyy",
        })}
      onChange={(date) => {
        setDate(date);
        if (showTimeSelectOnly) setOpen(false);
      }}
      disabled={isDisabled}
      customInput={
        <CalendarInput
          className={classNames("w100", {
            isEndDate: isEndDateCalendar,
            disabled: isDisabled,
          })}
          placeholder={placeholder}
          disabled={isDisabled}
        />
      }
      renderCustomHeader={renderHeader}
    />
  );
};

export default function DiscountDuration({ discountDuration = null, onChange = () => { } }) {
  // helper to combine date and time
  const combineDateAndTime = (dateString, timeString) => {
    const date = new Date(dateString);
    if (timeString) {
      const [hours, minutes] = timeString.split(":").map(Number);
      date.setHours(hours, minutes, 0, 0);
    }
    return date;
  };

  // Default initialization
  const [startDate, setStartDate] = useState(
    discountDuration?.defaultStartDate
      ? new Date(discountDuration.defaultStartDate)
      : new Date()
  );

  const [startTime, setStartTime] = useState(
    discountDuration?.defaultStartTime
      ? combineDateAndTime(new Date(), discountDuration.defaultStartTime)
      : new Date()
  );

  const [showEnd, setShowEnd] = useState(
    !!(discountDuration?.defaultEndDate && discountDuration?.defaultEndTime)
  );

  const [endDate, setEndDate] = useState(
    discountDuration?.defaultEndDate
      ? new Date(discountDuration.defaultEndDate)
      : new Date(new Date().setDate(new Date().getDate() + 1))
  );

  const [endTime, setEndTime] = useState(
    discountDuration?.defaultEndTime
      ? combineDateAndTime(new Date(), discountDuration.defaultEndTime)
      : new Date()
  );

  // 🔹 Ensure End Date is always at least 1 day after Start Date
  useEffect(() => {
    if (!showEnd) return;

    const nextDay = new Date(startDate);
    nextDay.setDate(startDate.getDate() + 1);

    if (endDate <= startDate) {
      setEndDate(nextDay);
    }
  }, [startDate]);

  // 🔹 Clear end date/time when unchecked
  useEffect(() => {
    if (!showEnd) {
      setEndDate(null);
      setEndTime(null);
    }
    else if (showEnd && !endTime) {
      setEndTime(new Date());
      const nextDay = new Date(startDate);
      nextDay.setDate(startDate.getDate() + 1);

      if (endDate <= startDate) {
        setEndDate(nextDay);
      }
    }
  }, [showEnd]);

  useEffect(() => {
    onChange({
      startDate,
      startTime,
      endDate,
      endTime,
    });
  }, [startDate, startTime, endDate, endTime]);

  return (
    <div className="discount-duration">
      {/* Start Date + Time */}
      <div className="row">
        <div className="input-rapper">
          <p className="label">Start Date</p>
          <BaseCalendar
            date={startDate}
            setDate={setStartDate}
            placeholder="Select Start Date"
          />
        </div>
        <div className="input-rapper">
          <p className="label">Start Time</p>
          <BaseCalendar
            date={startTime}
            setDate={setStartTime}
            placeholder="Select Time"
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            isTimePicker
          />
        </div>
      </div>

      <div className="checkbox-row">
        <CheckBox
          label="Set End Date and Time"
          checked={showEnd}
          onChange={(e) => setShowEnd(e.target.checked)}
        />
      </div>


      {/* End Date + Time */}
      {showEnd && (
        <div className="row">
          <div className="input-rapper">
            <p className="label">End Date</p>
            <BaseCalendar
              date={endDate}
              setDate={setEndDate}
              placeholder="Select End Date"
              isEndDateCalendar
              trackStartDate={startDate}
            />
          </div>
          <div className="input-rapper">
            <p className="label">End Time</p>
            <BaseCalendar
              date={endTime}
              setDate={setEndTime}
              placeholder="Select Time"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              isTimePicker
            />
          </div>
        </div>
      )}
    </div>
  );
}
